/**
 * VoiceFAB — floating action button for the onboarding voice assistant.
 * Uses Lucide-style inline SVG icons; the state shows in the icon and the ring. Idle is the orange
 * "start" action, every live state is violet (SAM), and errors are red.
 * Replaces the RobotAvatar PNG in the onboarding context.
 */

import type { VoiceStatus } from '@/redux/slices/onboardingVoiceSlice'
import { cn } from '@/utils/cn'

interface VoiceFABProps {
  status: VoiceStatus
  onClick?: () => void
  title?: string
  size?: 'sm' | 'md' | 'lg'
}

const STATE_CLASS: Record<VoiceStatus, string> = {
  idle:       'bg-primary text-on-primary shadow-glow-primary',
  connecting: 'bg-accent-fill text-on-accent shadow-glow-accent',
  listening:  'bg-accent-fill text-on-accent shadow-glow-accent',
  thinking:   'bg-accent-fill text-on-accent shadow-glow-accent',
  speaking:   'bg-accent-fill text-on-accent shadow-glow-accent',
  error:      'bg-danger-fill text-white',
}

const SIZE_CLASS = {
  sm: 'w-12 h-12',
  md: 'w-14 h-14',
  lg: 'w-16 h-16',
}

const ICON_SIZE = {
  sm: 20,
  md: 22,
  lg: 26,
}

const svgProps = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true }

/** Mic icon (idle / listening) */
function MicIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} strokeWidth="2" {...svgProps}>
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />
    </svg>
  )
}

/** MicOff icon (error) */
function MicOffIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} strokeWidth="2" {...svgProps}>
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23M12 19v4M8 23h8" />
    </svg>
  )
}

/** AudioLines / waveform icon (speaking) */
function AudioLinesIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} strokeWidth="2" {...svgProps}>
      <line x1="2"  y1="12" x2="2"  y2="12" />
      <line x1="6"  y1="8"  x2="6"  y2="16" />
      <line x1="10" y1="5"  x2="10" y2="19" />
      <line x1="14" y1="8"  x2="14" y2="16" />
      <line x1="18" y1="10" x2="18" y2="14" />
      <line x1="22" y1="12" x2="22" y2="12" />
    </svg>
  )
}

/** Spinner icon (connecting / thinking) */
function SpinnerIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} strokeWidth="2.5" {...svgProps} className="animate-spin">
      <path d="M21 12a9 9 0 11-6.219-8.56" />
    </svg>
  )
}

export function VoiceFAB({ status, onClick, title, size = 'md' }: VoiceFABProps) {
  const iconSize = ICON_SIZE[size]
  const isActive = status !== 'idle' && status !== 'error'

  const icon = (() => {
    switch (status) {
      case 'speaking':
        return <AudioLinesIcon size={iconSize} />
      case 'connecting':
      case 'thinking':
        return <SpinnerIcon size={iconSize} />
      case 'error':
        return <MicOffIcon size={iconSize} />
      default:
        return <MicIcon size={iconSize} />
    }
  })()

  return (
    <div className="relative flex-shrink-0">
      {/* Expanding ring on listening / speaking */}
      {(status === 'listening' || status === 'speaking') && (
        <span
          className="absolute inset-0 rounded-full bg-accent animate-ping opacity-30 pointer-events-none"
        />
      )}

      <button
        onClick={onClick}
        title={title ?? (isActive ? 'Stop Voice Mode' : 'Start Voice Mode')}
        aria-label={title ?? (isActive ? 'Stop Voice Mode' : 'Start Voice Mode')}
        className={cn(
          SIZE_CLASS[size],
          STATE_CLASS[status],
          'rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-card hover:scale-105 active:scale-95',
        )}
      >
        {icon}
      </button>
    </div>
  )
}
